import { useSuspenseQuery } from "@tanstack/react-query";
import { Suspense, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { Select } from "@/components/catalyst/Select";
import { orpc } from "@/orpc/client";
import { StatCard } from "@/pages/dashboard/components/StatCard";
import { formatCurrency } from "@/util/format";

const TIME_RANGE_OPTIONS = [
	{ value: 7, label: "Last 7 days" },
	{ value: 30, label: "Last 30 days" },
	{ value: 90, label: "Last 90 days" },
	{ value: 365, label: "Last year" },
] as const;

interface AppStatisticsContentProps {
	appId: string;
	days: number;
}

function AppStatisticsContent({ appId, days }: AppStatisticsContentProps) {
	const { data: stats } = useSuspenseQuery(
		orpc.apps.statistics.queryOptions({
			input: { appId, days },
		}),
	);

	return (
		<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
			<StatCard
				label="Total Revenue"
				value={formatCurrency(stats.totalRevenue, stats.currency)}
				description="Excludes IAP fees & taxes"
			/>
			<StatCard
				label="Partner Earnings"
				value={formatCurrency(stats.totalPartnerEarnings, stats.currency)}
				description="Total earned by partners"
			/>
			<StatCard
				label="Pending Payouts"
				value={formatCurrency(stats.pendingPartnerPayouts, stats.currency)}
				description="Accrued, not yet paid"
			/>
			<StatCard
				label="Total Installs"
				value={stats.totalInstalls.toLocaleString()}
				description="Claimed referrals"
			/>
			<StatCard
				label="Top Link"
				value={stats.topLink?.slug ?? "—"}
				description={
					stats.topLink
						? `${stats.topLink.claimCount} installs · ${formatCurrency(stats.topLink.revenue, stats.currency)}`
						: "No activity yet"
				}
			/>
		</div>
	);
}

export function AppStatisticsSkeleton() {
	return (
		<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
			{[1, 2, 3, 4, 5].map((i) => (
				<div
					key={i}
					className="rounded-xl border border-base-content/10 bg-base-100 p-6"
				>
					<Skeleton width={80} height={16} />
					<Skeleton width={120} height={36} className="mt-2" />
					<Skeleton width={100} height={12} className="mt-1" />
				</div>
			))}
		</div>
	);
}

interface AppStatisticsProps {
	appId: string;
}

/**
 * App-level statistics summary with period selector.
 * Shows aggregate stats across all referral links.
 */
export function AppStatistics({ appId }: AppStatisticsProps) {
	const [days, setDays] = useState<number>(30);

	return (
		<div className="mb-8">
			<div className="mb-4 flex items-center justify-between">
				<div>
					<h3 className="text-lg font-semibold text-base-content">
						Performance Overview
					</h3>
					<p className="mt-1 text-sm text-base-content/60">
						See how your referral program is performing
					</p>
				</div>
				<Select
					value={days}
					onChange={(e) => setDays(Number(e.target.value))}
					className="w-48!"
				>
					{TIME_RANGE_OPTIONS.map((option) => (
						<option key={option.value} value={option.value}>
							{option.label}
						</option>
					))}
				</Select>
			</div>
			<Suspense fallback={<AppStatisticsSkeleton />}>
				<AppStatisticsContent appId={appId} days={days} />
			</Suspense>
		</div>
	);
}
